/* FdFarmRiskForm.tsx
   – layout mirrors the Farm / Customer / Supplier forms */

import React from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import {Controller, useFieldArray, useForm} from 'react-hook-form';

/* ───────── types ───────── */
export type RiskRow = {
  id?: number;
  riskId: string | number;
  problem: string;
  mitigation: string;
};
export type Submission = {rows: RiskRow[]};

/* ───────── component ───────── */
export default function FdFarmRiskForm(props: {
  risks: {id: string; name: string}[];
  defaultRows: RiskRow[];
  isLoading: boolean;
  onSubmit: (d: Submission) => void;
  onCancel: () => void;
}) {
  const {control, handleSubmit} = useForm<Submission>({
    defaultValues: {rows: props.defaultRows},
  });
  const {fields, append, remove} = useFieldArray({control, name: 'rows'});

  return (
    <div className="generic-form-container fd-risk-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(props.onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2">Top risks</Typography>
            </Grid>

            {fields.map((field, idx) => (
              <React.Fragment key={field.id}>
                {/* risk type */}
                <Grid item xs={12} sm={3}>
                  <Controller
                    name={`rows.${idx}.riskId`}
                    control={control}
                    rules={{required: true}}
                    render={({field}) => (
                      <TextField
                        {...field}
                        select
                        label="Risk"
                        variant="outlined"
                        fullWidth
                        SelectProps={{native: true}}
                      >
                        <option value="" />
                        {props.risks.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* problem */}
                <Grid item xs={12} sm={4}>
                  <Controller
                    name={`rows.${idx}.problem`}
                    control={control}
                    rules={{required: true}}
                    render={({field}) => (
                      <TextField
                        {...field}
                        label="Problem"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>

                {/* mitigation */}
                <Grid item xs={12} sm={4}>
                  <Controller
                    name={`rows.${idx}.mitigation`}
                    control={control}
                    rules={{required: true}}
                    render={({field}) => (
                      <TextField
                        {...field}
                        label="Mitigation"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>

                {/* remove */}
                <Grid
                  item
                  xs={12}
                  sm={1}
                  style={{display: 'flex', alignItems: 'center'}}
                >
                  <Button onClick={() => remove(idx)}>Remove</Button>
                </Grid>
              </React.Fragment>
            ))}

            {/* add button */}
            <Grid item xs={12}>
              <Button
                onClick={() =>
                  append({riskId: '', problem: '', mitigation: ''})
                }
              >
                Add
              </Button>
            </Grid>
          </Grid>

          {/* footer actions */}
          <Grid container spacing={2} style={{marginTop: '1rem'}}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={props.onCancel}
                disabled={props.isLoading}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={props.isLoading}
              >
                {props.isLoading ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
